import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import Add from '@mui/icons-material/Add'
import Close from '@mui/icons-material/Close'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import Edit from '@mui/icons-material/Edit'
import FileUpload from '@mui/icons-material/FileUpload'
import Visibility from '@mui/icons-material/Visibility'
import { toast } from 'react-toastify'
import { useFilePondUpload } from 'src/consumer/components/Helpers/PictureUploadComponent/useFilePondUpload'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { useImageUpload } from '../hooks/useImageUpload'
import PrescriptionPreviewModal from './PrescriptionPreviewModal'

function getFileExtension(blobType) {
  if (!blobType) {
    return 'jpg'
  }
  return blobType.split('/')?.[1] || 'jpg'
}

function getFileFromDokaOutput(output, fallbackName) {
  const candidates = [
    output?.file,
    output?.dest,
    output?.blob,
    output?.data,
    output,
  ]

  const outputFile = candidates.find((candidate) => {
    if (typeof globalThis.File !== 'undefined' && candidate instanceof globalThis.File) {
      return true
    }
    return typeof globalThis.Blob !== 'undefined' && candidate instanceof globalThis.Blob
  })

  if (!outputFile) {
    return null
  }

  if (typeof globalThis.File !== 'undefined' && outputFile instanceof globalThis.File) {
    return outputFile
  }

  return new globalThis.File([outputFile], fallbackName, {
    type: outputFile.type || 'image/jpeg',
  })
}

async function fetchImageAsFile(imageUrl, fallbackName) {
  const response = await fetch(imageUrl, { mode: 'cors' })
  if (!response.ok) {
    throw new Error('Unable to load image for editing')
  }
  const blob = await response.blob()
  const extension = getFileExtension(blob.type)
  return new globalThis.File([blob], `${fallbackName}.${extension}`, {
    type: blob.type || 'image/jpeg',
  })
}

/**
 * Self-contained component for image upload with prescription preview and height adjustment
 * Handles: Upload → Crop → Preview Modal → Height Adjustment → Server Upload → Callback
 */
export default function ImageUploadWithHeightAdjustment({
  imageId,
  currentHeight,
  prescriptionTemplate,
  type, // 'header' or 'footer'
  defaultHeight = 2,
  minHeight = 0.5,
  maxHeight = 5,
  onComplete, // (imageId, adjustedHeight) => void
  disabled,
  placeholder = 'Add Image',
  aspectWidth,
  aspectHeight,
  showSlider,
}) {
  const [modalOpen, setModalOpen] = useState(false)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [deferredFile, setDeferredFile] = useState(null)
  const [localBlobUrl, setLocalBlobUrl] = useState(null)
  const [isEditingExistingImage, setIsEditingExistingImage] = useState(false)

  const { uploadImage, isUploading } = useImageUpload()
  const imageUrl = imageId ? getImageLink(imageId) : null
  const title = type.charAt(0).toUpperCase() + type.slice(1)

  // Use deferred upload mode - don't upload until user confirms
  const {
    triggerUpload,
    isUploading: isProcessing,
    clearDeferredFile,
    FilePondComponent,
  } = useFilePondUpload({
    aspectWidth,
    aspectHeight,
    deferUpload: true, // Key: defer upload until confirmation
    onEditComplete: ({ file, blobUrl }) => {
      // Doka editing complete - store file and open modal
      setDeferredFile(file)
      setLocalBlobUrl(blobUrl)
      setModalOpen(true)
    },
  })

  // Cleanup blob URL on unmount or when cleared
  useEffect(() => {
    return () => {
      if (localBlobUrl) {
        URL.revokeObjectURL(localBlobUrl)
      }
    }
  }, [localBlobUrl])

  const handleRemove = (e) => {
    e?.stopPropagation?.()
    onComplete(null, currentHeight)
    setViewerOpen(false)
  }

  const handleClick = () => {
    if (!disabled) {
      if (imageId) {
        setViewerOpen(true)
        return
      }
      triggerUpload()
    }
  }

  const handleReplace = () => {
    if (disabled) return
    setViewerOpen(false)
    triggerUpload()
  }

  const handleEditExistingImage = async () => {
    if (!imageUrl || disabled) return

    let editor
    setIsEditingExistingImage(true)
    try {
      const { createDokaEditor } = await import('src/consumer/helpers/dokaLoader')
      const existingFile = await fetchImageAsFile(imageUrl, `${type}-image`)
      const cssAspectRatio = aspectWidth && aspectHeight ? aspectWidth / aspectHeight : undefined
      editor = await createDokaEditor({
        cropAspectRatio: cssAspectRatio ? 1 / cssAspectRatio : undefined,
      })
      const output = await editor.edit(existingFile)
      const editedFile = getFileFromDokaOutput(output, `${type}-image-edited.jpg`)

      if (!editedFile) {
        throw new Error('Image editor did not return an edited file')
      }

      const blobUrl = URL.createObjectURL(editedFile)
      setDeferredFile(editedFile)
      setLocalBlobUrl(blobUrl)
      setViewerOpen(false)
      setModalOpen(true)
    } catch (error) {
      if (error?.message !== 'cancel') {
        console.error('Failed to edit existing image:', error)
        toast.error('Image editor failed to load this image. Please try replacing it.')
      }
    } finally {
      try {
        editor?.destroy?.()
      } catch (_error) {
        // Editor may already be destroyed after closing.
      }
      setIsEditingExistingImage(false)
    }
  }

  const handleModalClose = () => {
    if (!isUploading) {
      setModalOpen(false)
      // Clean up deferred file and blob URL
      if (localBlobUrl) {
        URL.revokeObjectURL(localBlobUrl)
      }
      setDeferredFile(null)
      setLocalBlobUrl(null)
      clearDeferredFile()
    }
  }

  const handleModalConfirm = async (adjustedHeight) => {
    if (!deferredFile) return

    // Upload file to server
    const uploadedImageId = await uploadImage(deferredFile)

    if (uploadedImageId) {
      // Success - callback with imageId and height
      onComplete(uploadedImageId, adjustedHeight)
      handleModalClose()
    }
    // If upload failed, keep modal open so user can try again or cancel
  }

  return (
    <>
      <FilePondComponent />

      {/* Upload/Preview Box */}
      <Box
        onClick={handleClick}
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: 200,
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          overflow: 'hidden',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
          '&:hover': !disabled && {
            borderColor: '#1976d2',
            backgroundColor: '#fafafa',
          },
        }}
      >
        {isProcessing && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!imageId && !isProcessing && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              color: '#1976d2',
            }}
          >
            <Add sx={{ fontSize: 48 }} />
            <Box sx={{ fontSize: 16, fontWeight: 500 }}>{placeholder}</Box>
          </Box>
        )}

        {imageId && !isProcessing && (
          <>
            {!disabled && (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 5,
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Visibility />}
                  onClick={(e) => {
                    e.stopPropagation()
                    setViewerOpen(true)
                  }}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: 'rgba(0, 0, 0, 0.64)',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.78)' },
                  }}
                >
                  View
                </Button>
                <IconButton
                  onClick={handleRemove}
                  sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.64)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.78)',
                    },
                  }}
                  size="small"
                >
                  <Close />
                </IconButton>
              </Stack>
            )}
            <Box
              component="img"
              src={imageUrl}
              alt="Preview"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </>
        )}
      </Box>

      <Dialog open={viewerOpen} onClose={() => setViewerOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{title} image</Typography>
          <IconButton onClick={() => setViewerOpen(false)} aria-label="Close">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f6f6f6' }}>
          <Box
            sx={{
              minHeight: { xs: 260, md: 520 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {imageUrl && (
              <Box
                component="img"
                src={imageUrl}
                alt={`${type} preview`}
                sx={{
                  width: '100%',
                  maxHeight: '72vh',
                  objectFit: 'contain',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<FileUpload />}
            onClick={handleReplace}
            disabled={disabled || isEditingExistingImage}
          >
            Replace
          </Button>
          <Button
            variant="contained"
            startIcon={isEditingExistingImage ? <CircularProgress size={16} /> : <Edit />}
            onClick={handleEditExistingImage}
            disabled={disabled || isEditingExistingImage}
          >
            {isEditingExistingImage ? 'Opening editor...' : 'Edit image'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutline />}
            onClick={handleRemove}
            disabled={disabled || isEditingExistingImage}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Modal */}
      <PrescriptionPreviewModal
        open={modalOpen}
        onClose={handleModalClose}
        prescriptionTemplate={prescriptionTemplate}
        blobUrl={localBlobUrl}
        type={type}
        defaultHeight={currentHeight || defaultHeight}
        minHeight={minHeight}
        maxHeight={maxHeight}
        onConfirm={handleModalConfirm}
        isUploading={isUploading}
        showSlider={showSlider}
      />
    </>
  )
}
